import { SelectItem } from 'primeng/api';
import { InvoicePaymentUpsertDto } from './../../dto/invoice-payment-upsert.dto';
import { InvoiceLineItemUpsertDto } from './../../dto/invoice-line-item-upsert.dto';
import { AdjustmentTypeService } from './../../../revenue/shared/adjustment-types/adjustment-type.service';
import { InvoiceAdjustmentUpsertDto } from './../../dto/invoice-adjustment-upsert.dto';
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AdjustmentType } from 'src/app/revenue/shared/adjustment-types/adjustment-type.model';
import { RevenueItem } from 'src/app/revenue/shared/revenue-items/revenue-item.model';
import { PaymentTypeService } from 'src/app/revenue/shared/payment-types/payment-type.service';
import { PaymentTypeDto } from 'src/app/revenue/shared/payment-types/dto/payment-type.dto';
import { AdjustmentService } from 'src/app/revenue/shared/adjustment/adjustment.service';
import { min } from 'rxjs/operators';
import { RevenueObservationService } from 'src/app/revenue/shared/revenue-observation.service';

@Component({
  selector: 'wl-invoice-adjustment-edit',
  templateUrl: './invoice-adjustment-edit.component.html',
  styleUrls: ['./invoice-adjustment-edit.component.less'],
    standalone: false
})
export class InvoiceAdjustmentEditComponent implements OnInit, OnChanges {
  @Input() adjustment: InvoiceAdjustmentUpsertDto;
  @Input() lineItem: InvoiceLineItemUpsertDto;
  @Input() payment: InvoicePaymentUpsertDto;
  @Input() flatAdjustmentIds: Array<number>;
  @Input() adjustmentTypes: Array<AdjustmentType>;
  @Input() allRevenueItems: RevenueItem[];
  public isAltRow = false;
  public adjustmentTypeItems: SelectItem[] = [];
  public paymentType: PaymentTypeDto;
  public debitAccount: string;
  public creditAccount: string;
  private _cleanAdjustment: string;

  public get adjustmentSign() {
    if (this._adjustmentIsNegative) {
      return '-';
    } else {
      return '+';
    }
  }

  private _adjustmentType: AdjustmentType;
  private _adjustmentIsNegative = true;

  constructor(
    private _paymentTypeService: PaymentTypeService, 
    private _adjustmentService: AdjustmentService,
    private _revenueObservationService: RevenueObservationService
  ) {}

  ngOnInit() {
    this._revenueObservationService.observeRequestInvoiceUpsert().subscribe(() => {
      this.setDirty();
    });
    this._revenueObservationService.observeInvoiceLoaded().subscribe(() => {
      this.setCleanObject();
    });
    this.setCleanObject();
  }

  get dirty() {
    return JSON.stringify(this.adjustment) != this._cleanAdjustment
  }
  
  private setDirty() {
    this.adjustment.Dirty = this.dirty;
  }

  private setCleanObject() {
    this._cleanAdjustment = JSON.stringify(this.adjustment);
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.adjustmentTypes && this.adjustmentTypes) {
      this.adjustmentTypeItems = this.adjustmentTypes.map<SelectItem>((x) => {
        return { label: x.AdjustmentType, value: x.AdjustmentTypeId };
      });
    }

    if (this.adjustment && this.flatAdjustmentIds) {
      this.isAltRow = this.flatAdjustmentIds.findIndex((x) => x === this.adjustment.AdjustmentId) % 2 !== 0;
      this._adjustmentIsNegative = this.adjustment.AdjustmentAmount <= 0;
    }

    if (simpleChanges.payment && this.payment) {
      this._paymentTypeService.getPaymentType(this.payment.PaymentTypeID).then((type) => {
        this.paymentType = type;
        this.updateAccounts();
      });
    }

    if (this.adjustment && this.adjustmentTypes) {
      this.updateAccounts();
    }
  }

  public flipAdjustmentSign() {
    this._adjustmentIsNegative = !this._adjustmentIsNegative;
    this.adjustment.AdjustmentAmount *= -1;
  }

  public formatAdjustmentAmount() {
    if (
      (this._adjustmentIsNegative && this.adjustment.AdjustmentAmount > 0) ||
      (!this._adjustmentIsNegative && this.adjustment.AdjustmentAmount < 0)
    ) {
      this.adjustment.AdjustmentAmount *= -1;
    }
  }

  public deleteAdjustment() {
    this.adjustment.DeleteAdjustment = true;
  }

  updateAccounts() {
    if (this.adjustment.Posted) {
      this._adjustmentService.getPostedTransactions(this.adjustment.AdjustmentId).then((transactions) => {
        const creditTransaction = transactions.find((x) => x.CreditAmount > 0);
        const debitTransaction = transactions.find((x) => x.DebitAmount > 0);
        this.creditAccount = creditTransaction?.Account;
        this.debitAccount = debitTransaction?.Account;
      });
    } else {
      const adjustmentType = this.adjustmentTypes?.find((x) => x.AdjustmentTypeId === this.adjustment.AdjustmentTypeId);
      if (adjustmentType?.ReversingEntry) {
        if (this.adjustment?.PaymentId) {
          this.creditAccount = this.paymentType?.DebitAccount;
          this.debitAccount = this.paymentType?.CreditAccount;
        } else if (this.adjustment.InvoiceLineItemId) {
          const revenueItem = this.allRevenueItems?.find((x) => x.ItemNum === this.lineItem?.ItemNum);
          this.creditAccount = revenueItem?.DebitAccount;
          this.debitAccount = revenueItem?.CreditAccount;
        }
      } else {
        this.creditAccount = adjustmentType?.CreditAccount;
        this.debitAccount = adjustmentType?.DebitAccount;
      }
    }
  }

  getItemDescription(itemNum: number) {
    const revItem = this.allRevenueItems.find((x) => x.ItemNum === itemNum);
    if (revItem) {
      return revItem.Descr;
    } else {
      return '';
    }
  }
}
